import { useFileUpload } from './useFileUpload';
import { useOrderState } from './useOrderState';
import { useUploadState } from './useUploadState';
import { useModalText } from './useModalText';
import { useModalHandlers } from './useModalHandlers';
import { UseUploadModalProps, UseUploadModalReturn } from '../types';

/**
 * @description Main hook for the upload modal - now composed of smaller focused hooks
 * @param onClose - The function to call when the modal is closed
 * @param orderId - The ID of the order
 * @returns Complete upload modal state and actions
 */
export const useUploadModal = ({
  onClose,
  orderId,
}: UseUploadModalProps): UseUploadModalReturn => {

  const {
    finalOrderId,
    orderDetails,
    orderLoading,
    orderError,
    seatRequirements,
    hasSeatProducts,
  } = useOrderState({ orderId });

  const {
    files,
    isUploading,
    uploadProgress,
    fileProgress,
    error,
    uploadResults,
    handleFileInput,
    removeFile,
    handleUpload,
    handleCloseModal,
  } = useFileUpload({
    maxFiles: seatRequirements?.totalPhotosRequired || 10,
  });

  const {
    requiredPhotosUploaded,
    canUploadMore,
    maxFiles,
  } = useUploadState({
    filesCount: Object.keys(files).length,
    seatRequirements,
  });

  const {
    modalTitle,
    loadingText,
    noPhotosRequiredTitle,
    noPhotosRequiredMessage,
    allPhotosUploadedTitle,
    allPhotosUploadedMessage,
    cancelButtonText,
    submitButtonText,
    uploadingButtonText,
    successMessage,
  } = useModalText({
    uploadResultsCount: uploadResults.length,
    uploadProgress,
    totalPhotosRequired: seatRequirements?.totalPhotosRequired || null,
  });

  const {
    handleClose,
    handleCancel,
    handleUploadClick,
  } = useModalHandlers({
    finalOrderId,
    orderDetails,
    files: Object.values(files).filter((f): f is File => f !== null), // Convert record to array for handlers if needed, or update handlers
    seatRequirements,
    onClose,
    handleCloseModal,
    handleUpload,
  });

  return {
    orderDetails,
    orderLoading,
    orderError,
    finalOrderId,
    seatRequirements,
    hasSeatProducts,
    files,
    isUploading,
    uploadProgress,
    fileProgress,
    error,
    uploadResults,
    maxFiles,
    requiredPhotosUploaded,
    canUploadMore,
    handleFileInput,
    removeFile,
    handleClose,
    handleCancel,
    handleUploadClick,
    modalTitle,
    loadingText,
    noPhotosRequiredTitle,
    noPhotosRequiredMessage,
    allPhotosUploadedTitle,
    allPhotosUploadedMessage,
    successMessage,
    cancelButtonText,
    submitButtonText,
    uploadingButtonText,
  };
};
